import { type ReactElement } from 'react'
import { LANGUAGE_OPTIONS } from '@util'
import { isUserId, type LockableUserField, type UnsavedUser, type UserId } from '@terros/common'
import { type UseFormReturnType } from '@mantine/form'
import { ColorInput, Grid, MultiSelect, Select } from '@mantine/core'
import { getTimeZones, RANDOM_COLORS, useLockedUserFields, useTranslation } from '@front/model'
import { LockedFieldTooltip } from '../../permissions'

type Props<T extends UnsavedUser> = {
  form: UseFormReturnType<T>
}

export const UserPreferencesForm = <T extends UnsavedUser>({ form }: Props<T>): ReactElement => {
  const { getInputProps } = form
  const { t } = useTranslation()
  const { lockedFields } = useLockedUserFields(getUserId(form))
  const isLocked = (field: LockableUserField): boolean => lockedFields.includes(field)

  return (
    <>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <LockedFieldTooltip locked={isLocked('language')}>
            <Select {...getInputProps('language')} label={t('Primary Language')} data={LANGUAGE_OPTIONS} searchable />
          </LockedFieldTooltip>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <MultiSelect
            {...getInputProps('secondaryLanguages')}
            label={t('Secondary Languages')}
            data={LANGUAGE_OPTIONS}
            searchable
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <LockedFieldTooltip locked={isLocked('timeZone')}>
            <Select
              {...getInputProps('timeZone')}
              label={t('Timezone')}
              data={getTimeZones(form.values.timeZone)}
              searchable
              allowDeselect={false}
            />
          </LockedFieldTooltip>
        </Grid.Col>
      </Grid>
      <ColorInput
        {...getInputProps('color')}
        label={t('User Color')}
        description={t('UI color theme for the user (default avatar color, default area assignment color, etc.)')}
        swatches={RANDOM_COLORS}
      />
    </>
  )
}

const getUserId = <T extends UnsavedUser>(form: UseFormReturnType<T>): UserId | undefined => {
  const userId = 'userId' in form.values ? form.values.userId : undefined
  if (isUserId(userId)) return userId
}
